// Print Center P3 — admin SPA client for /api/admin/printers/*.
//
// Mirrors the route surface in apps/api/src/routes/printers.ts. The wire
// shape is `PrinterAdminView` (= Printer with Epson tokens stripped); we
// import it from @hub-wifi/shared so the SPA and API can't drift.
//
// Every function takes the Auth0 access `token` explicitly — matches the
// pattern in apps/admin/src/api/rooms.ts and keeps the call sites honest
// about which actions are admin-gated.
import { apiRequest, ApiError } from './client'
import type { PrinterAdminView, PrinterJobDto, PrinterStatus } from '@hub-wifi/shared'

const API_URL = import.meta.env.VITE_API_URL as string

export type { PrinterStatus, PrinterAdminView, PrinterJobDto }

/** Alias preserved for readability at call sites. */
export type PrinterDto = PrinterAdminView

export interface StartOnboardingResult {
  printerId: string
  authUrl: string
}

export interface TestPrintResult {
  jobId: string
  status: 'initiated'
}

export interface ReactivateResult {
  status: 'active' | 'needs_reoauth'
}

/**
 * List printers, optionally filtered by buildingId. Status filter is
 * intentionally not surfaced yet — the list page renders the badge per row
 * and admins can eyeball the (small) set.
 */
export function listPrinters(token: string, buildingId?: string): Promise<PrinterDto[]> {
  // The backend route accepts `building_id` (snake_case query param) — see
  // listPrintersQuerySchema in validators/printerSchema.ts.
  const path = buildingId
    ? `/api/admin/printers?building_id=${encodeURIComponent(buildingId)}`
    : '/api/admin/printers'
  return apiRequest<PrinterDto[]>(path, { token })
}

export function getPrinter(token: string, printerId: string): Promise<PrinterDto> {
  return apiRequest<PrinterDto>(`/api/admin/printers/${printerId}`, { token })
}

/** Printer Detail "recent jobs" table — GET /api/admin/printers/:id/jobs?limit= */
export function getPrinterJobs(
  token: string,
  printerId: string,
  limit = 20,
): Promise<PrinterJobDto[]> {
  return apiRequest<PrinterJobDto[]>(
    `/api/admin/printers/${printerId}/jobs?limit=${encodeURIComponent(String(limit))}`,
    { token },
  )
}

export function startOnboarding(
  token: string,
  input: { name: string; buildingId: string },
): Promise<StartOnboardingResult> {
  return apiRequest<StartOnboardingResult>('/api/admin/printers/start', {
    method: 'POST',
    body: input,
    token,
  })
}

export function updatePrinter(
  token: string,
  printerId: string,
  patch: { name?: string; buildingId?: string },
): Promise<PrinterDto> {
  return apiRequest<PrinterDto>(`/api/admin/printers/${printerId}`, {
    method: 'PATCH',
    body: patch,
    token,
  })
}

export function deletePrinter(token: string, printerId: string): Promise<{ ok: true }> {
  return apiRequest<{ ok: true }>(`/api/admin/printers/${printerId}`, {
    method: 'DELETE',
    token,
  })
}

/**
 * Selects which physical PDF the API renders + submits to Epson:
 *   - `diagnostic`: integration-test page (default; original behavior).
 *     Only this kind promotes `pending_verification → active`.
 *   - `qr`: the QR sticker. Sanity check for an already-active printer;
 *     never promotes. Backend rejects with PRINTER_NOT_TESTABLE when
 *     the printer isn't `active`.
 */
export type TestPrintKind = 'diagnostic' | 'qr'

export function runTestPrint(
  token: string,
  printerId: string,
  kind: TestPrintKind = 'diagnostic',
  reason?: string,
): Promise<TestPrintResult> {
  const body: { kind: TestPrintKind; reason?: string } = { kind }
  if (reason) body.reason = reason
  return apiRequest<TestPrintResult>(`/api/admin/printers/${printerId}/test-print`, {
    method: 'POST',
    body,
    token,
  })
}

export function markPrinterVerified(token: string, printerId: string): Promise<{ ok: true }> {
  return apiRequest<{ ok: true }>(`/api/admin/printers/${printerId}/mark-verified`, {
    method: 'POST',
    token,
  })
}

export function deactivatePrinter(token: string, printerId: string): Promise<{ ok: true }> {
  return apiRequest<{ ok: true }>(`/api/admin/printers/${printerId}/deactivate`, {
    method: 'POST',
    token,
  })
}

export interface ConnectionCheckResult {
  epsonConnected: boolean
  connectionCheckedAt: string
  lastSeenConnectedAt: string | null
}

/** Live Epson Connect reachability probe — POST /api/admin/printers/:id/check-connection */
export function checkPrinterConnection(
  token: string,
  printerId: string,
): Promise<ConnectionCheckResult> {
  return apiRequest<ConnectionCheckResult>(`/api/admin/printers/${printerId}/check-connection`, {
    method: 'POST',
    token,
  })
}

export function reactivatePrinter(token: string, printerId: string): Promise<ReactivateResult> {
  return apiRequest<ReactivateResult>(`/api/admin/printers/${printerId}/reactivate`, {
    method: 'POST',
    token,
  })
}

/**
 * Submit an admin-uploaded PDF to Epson — "Send to print" → "Uploaded PDF"
 * option (docs/plans/2026-07-12-printer-detail-actions.md, Request #2c).
 * Bypasses the JSON-only `apiRequest` (which `JSON.stringify`s the body) in
 * favor of a raw `fetch` + `FormData`, mirroring `uploadPrintJob` in
 * `apps/admin/src/api/printJobs.ts` so binary bytes survive.
 */
export async function sendDocumentToPrint({
  token,
  printerId,
  file,
}: {
  token: string
  printerId: string
  file: File
}): Promise<TestPrintResult> {
  const form = new FormData()
  form.append('file', file, file.name)
  const resp = await fetch(`${API_URL}/api/admin/printers/${printerId}/print`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  })
  const body = await resp.json().catch(() => ({}))
  if (!resp.ok) {
    throw new ApiError(
      (body as { error?: string; code?: string }).error ?? 'Upload failed',
      resp.status,
      body,
    )
  }
  return body as TestPrintResult
}
